import { lazy, Suspense } from 'react';
import { createRootRouteWithContext, Outlet, Link } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { LiveEventsBridge } from '@/components/LiveEventsBridge';
import { ConfirmHost } from '@/components/ConfirmHost';
import { useThemeStore } from '@/lib/theme';
import { Button } from '@/components/ui/button';
import { Home } from 'lucide-react';
import { Starfield } from '@/components/baxter/Starfield';

// AB#428: guarded lazy import so the devtools chunk is never bundled in a built
// (PROD) build — it only loads under `pnpm dev`.
const TanStackRouterDevtools = import.meta.env.PROD
  ? () => null
  : lazy(() =>
      import('@tanstack/router-devtools').then((m) => ({ default: m.TanStackRouterDevtools })),
    );

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFoundPage,
});

function RootComponent() {
  const theme = useThemeStore((s) => s.theme);
  return (
    <>
      {/* The loader's sky, very faint, behind the admin in dark mode (shows where pages have no background); static, so free per frame. */}
      <Starfield className="-z-10 hidden opacity-35 dark:block" cols={14} rows={9} />
      <Outlet />
      <Toaster position="top-center" richColors theme={theme} />
      <LiveEventsBridge />
      <ConfirmHost />
      <Suspense fallback={null}>
        <TanStackRouterDevtools position="bottom-right" />
      </Suspense>
    </>
  );
}

// Floating orb positions
const orbs = [
  { top: '12%', left: '15%', size: 12, delay: '0s' },
  { top: '8%', left: '75%', size: 8, delay: '1s' },
  { top: '25%', left: '85%', size: 16, delay: '2s' },
  { top: '70%', left: '10%', size: 10, delay: '0.5s' },
  { top: '75%', left: '80%', size: 14, delay: '1.5s' },
  { top: '85%', left: '45%', size: 6, delay: '2.5s' },
];

function NotFoundPage() {
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center overflow-hidden bg-[radial-gradient(ellipse_at_center,var(--card)_0%,var(--background)_70%)]">
      {/* Floating orbs */}
      {orbs.map((orb, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-primary/20 blur-[1px] animate-not-found-drift"
          style={{
            top: orb.top,
            left: orb.left,
            width: orb.size,
            height: orb.size,
            animationDelay: orb.delay,
          }}
        />
      ))}

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center text-center px-6">
        <div className="not-found-404 font-black leading-none -tracking-wider text-primary select-none animate-not-found-fade-up">
          404
        </div>

        <div className="mt-4 mb-8 opacity-0 animate-not-found-fade-up [animation-delay:80ms]">
          <h1 className="text-2xl font-semibold text-foreground mb-2">
            Page not found
          </h1>
          <p className="text-muted-foreground max-w-md">
            The page you're looking for doesn't exist or has been moved.
          </p>
        </div>

        <div className="opacity-0 animate-not-found-fade-up [animation-delay:160ms]">
          <Button asChild size="lg">
            <Link to="/sites">
              <Home className="mr-2 h-4 w-4" />
              Back to home
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
